import { sendRawRequest } from "@api/clients/axios";
import { NotificationImportant } from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { getAdminNotificationPath } from "@repo/types";
import { formatRelative } from "@repo/utils/date";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MouseEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDateLocale } from "@/utils/date-locale";
import { translateNotification } from "@/utils/notificationText";
import {
  markNotificationRead,
  type NotificationItem,
  notificationPageQueryOptions,
} from "./notification-query";

type NotificationBellProps = {
  notifierId?: number;
  role: string;
};

export function NotificationBell({ notifierId, role }: NotificationBellProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { data: notificationCount = 0 } = useQuery({
    queryKey: ["notifications", notifierId, "unreadCount"],
    queryFn: async () => {
      const response = await sendRawRequest<undefined, number>("get", "notifier/unread/count");
      return response.data;
    },
    enabled: !!notifierId,
    refetchInterval: 30_000,
  });
  const {
    data: page,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...notificationPageQueryOptions(notifierId ?? 0, 1),
    enabled: !!notifierId,
  });

  const viewAll = () => {
    setAnchorEl(null);
    void navigate(`/${role}/notifications`);
  };
  const openNotification = (notification: NotificationItem) => {
    setAnchorEl(null);
    if (!notification.read_at && notifierId) {
      void markNotificationRead(notification.id)
        .then(() => queryClient.invalidateQueries({ queryKey: ["notifications", notifierId] }))
        .catch(() => undefined);
    }
    const path = getAdminNotificationPath(notification.metadata);
    void navigate(path ?? `/${role}/notifications`);
  };

  return (
    <>
      <Button
        color="inherit"
        aria-label={t("notifications.inbox.title")}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        sx={{ minWidth: "auto" }}
      >
        <Badge badgeContent={notificationCount} color="error">
          <NotificationImportant />
        </Badge>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: "min(400px, calc(100vw - 32px))",
              maxHeight: "min(560px, 75vh)",
              mt: 1,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {t("notifications.inbox.title")}
          </Typography>
          <Button size="small" onClick={viewAll}>
            {t("notifications.inbox.viewAll")}
          </Button>
        </Box>
        <Divider />
        {isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Alert
            severity="error"
            action={<Button onClick={() => void refetch()}>{t("document.review.retry")}</Button>}
          >
            {t("notifications.inbox.loadError")}
          </Alert>
        ) : page?.items.length ? (
          <Stack divider={<Divider />}>
            {page.items.slice(0, 5).map((notification) => {
              const text = translateNotification(t, notification);
              return (
                <ButtonBase
                  key={notification.id}
                  onClick={() => openNotification(notification)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    px: 2,
                    py: 1.5,
                    bgcolor: notification.read_at ? "background.paper" : "action.hover",
                    "&:hover": { bgcolor: "action.selected" },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2" fontWeight={notification.read_at ? 500 : 700}>
                      {text.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatRelative(notification.createdAt, { locale: dateLocale })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {text.content}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
            {t("notifications.inbox.empty")}
          </Typography>
        )}
      </Popover>
    </>
  );
}
